import {
  DITypes,
  EPluggyTypes,
  IUserProfile,
  LocalStorageKeys
} from "@/core/@types/types";
import { injectable, inject } from "inversify";
import { PluggyConnect } from "pluggy-connect-sdk";
import { type IHandleWebservice } from "../handlers/handleCriarWebservice";
import { type PluggyGateway } from "./ports/PluggyGateway";
import { type IToastNotification } from "@/composables/shared/notification/types";
import "reflect-metadata";
import { IContasResponseFin, IPluggyConnector } from "./types";
import {
  ISlackPayload,
  type ISlackMessage
} from "../../slackMessage/services/ports/SlackMessage";
import useStorage from "@/core/plugins/useStorage";

//CRIAR TESTES
@injectable()
export default class PluggyConnectorInit implements IPluggyConnector {
  @inject(EPluggyTypes.HANDLE_WEBSERVICE)
  private handleWebservice!: IHandleWebservice;

  @inject(EPluggyTypes.PLUGGY_GATEWAY)
  private pluggyGateway?: PluggyGateway;

  @inject(DITypes.NOTIFICATION)
  private notification!: IToastNotification<string>;

  @inject(DITypes.SLACK_MENSAGEM)
  private slackMensagem!: ISlackMessage;

  private onErrorDisplayed: boolean;

  private storage = useStorage();

  constructor() {
    this.onErrorDisplayed = false;
  }

  private instaciarWidget(
    token: string,
    connectorId: number | undefined,
    contaId: string
  ) {
    const pluggyConnect = new PluggyConnect({
      connectToken: token,
      includeSandbox: false,
      selectedConnectorId: connectorId || undefined,
      allowConnectInBackground: false,
      onSuccess: (itemData) => {
        //notificar SLACK

        const user = this.storage.get<IUserProfile>(
          LocalStorageKeys.UserProfile
        );

        const successModel = {
          NomeDoGrupo: user?.Grupo,
          Banco: itemData.item.conector.name,
          ContaBancariaId: contaId,
          ItemId: itemData.item.id,
          itemData: itemData.item
        };

        const payload: ISlackPayload = {
          message: `Sucesso na conexão de conta via widget Pluggy: ${successModel}`,
          channel: "#widget-pluggy-logs"
        };

        this.slackMensagem.enviarMensagem(payload);
        this.handleWebservice.handleCriarWebservice(
          itemData.item.id,
          contaId,
          this.onErrorDisplayed
        );

        pluggyConnect.hide();
        pluggyConnect.destroy();
      },
      onError: (error) => {
        //notificar SLACK

        const user = this.storage.get<IUserProfile>(
          LocalStorageKeys.UserProfile
        );

        const errorModel = {
          NomeDoGrupo: user?.Grupo,
          Conector: error?.data?.item.connector.name,
          ContaBancariaId: contaId,
          ItemId: error?.data?.item.id,
          ErrorPluggy: error
        };

        const payload: ISlackPayload = {
          message: `Erro na conexão de conta via widget Pluggy: ${errorModel}`,
          channel: "#widget-pluggy-logs"
        };
        this.slackMensagem.enviarMensagem(payload);
        console.error("Whoops! Pluggy Connect error... ", error);

        const data = error.data;
        this.onErrorDisplayed = true;

        const validErrors = ["USER_AUTHORIZATION_PENDING"];

        if (
          data?.item?.error?.code &&
          validErrors.includes(data.item.error.code)
        ) {
          this.handleWebservice.handleCriarWebservice(
            error?.data?.item.id,
            contaId,
            this.onErrorDisplayed
          );
        }
      },
      onClose: () => {
        pluggyConnect.hide();
      }
    });

    pluggyConnect.init();
  }

  hasCnpj(conta: IContasResponseFin | undefined): boolean | undefined {
    const hasCNPJ = conta?.Cnpj;

    if (!hasCNPJ) {
      this.notification.showNotification(
        "A conta selecionada não possui cadastrado um número de CNPJ. Por favor atualize suas informações na tela de Contas, para que seja possível conectar o Open Finance.",
        {
          type: "error",
          timeout: 3000
        }
      );

      return false;
    }

    return true;
  }

  async init(id: string) {
    const hasConta = await this.pluggyGateway?.obterConta(id);

    const hasCnpj = this.hasCnpj(hasConta);

    if (!hasCnpj) return;

    const connectByAccount =
      await this.pluggyGateway?.connectOpenFinanceByAccount(id);

    if (connectByAccount)
      return this.notification.showNotification(
        "Essa conta já possui conexão, confirme no webservice.",
        {
          type: "error",
          timeout: 3000
        }
      );

    const obterToken = await this.pluggyGateway?.obterConnectToken(
      connectByAccount || null
    );

    const obterConnectorId = await this.pluggyGateway?.obterConnectorId(id);

    if (!obterToken) {
      return this.notification.showNotification(
        "Houve um erro ao autenticar a conta, tente novamente mais tarde e se o erro persistir, entre em contato com o suporte.",
        {
          type: "error",
          timeout: 3000
        }
      );
    } else {
      try {
        this.instaciarWidget(obterToken, obterConnectorId, id);
      } catch (err) {
        console.log(err);
      }
    }
  }
}
